// packages/studio/web/src/repos/browser/FilePreview.tsx
//
// Right-pane read-only preview of a selected .arch file.
// Calls GET /api/ws/:slug/r/:repo/files/:ref/:path for blob content.
// Renders the content in a <pre> block (Monaco readonly would require
// the full editor bundle; this lightweight approach is sufficient for preview).
// An "Edit" button navigates to the full editor route.

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Spinner } from "../../ui/Spinner";
import { repoHome } from "../../lib/paths";

export interface FilePreviewProps {
  readonly wsSlug: string;
  readonly repoSlug: string;
  readonly ref: string;
  readonly path: string;
}

interface FilePayload {
  readonly path: string;
  readonly oid: string;
  readonly content: string;
}

export function FilePreview({ wsSlug, repoSlug, ref, path }: FilePreviewProps) {
  const q = useQuery({
    queryKey: ["file", wsSlug, repoSlug, ref, path],
    queryFn: async ({ signal }): Promise<FilePayload> => {
      const r = await fetch(
        `/api/ws/${encodeURIComponent(wsSlug)}/r/${encodeURIComponent(repoSlug)}/files/${encodeURIComponent(ref)}/${path}`,
        { credentials: "include", signal },
      );
      if (!r.ok) throw new Error(`file fetch failed: ${r.status}`);
      return r.json();
    },
    enabled: path.length > 0,
    staleTime: 30 * 1000,
  });

  if (!path) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-sm text-slate-600">
        Select a file to preview.
      </div>
    );
  }

  if (q.isPending) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-slate-500" aria-busy="true">
        <Spinner size="sm" />
        Loading…
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="p-4 text-sm text-red-400" role="alert">
        Failed to load file.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-slate-800 px-4 py-2">
        <span className="truncate font-mono text-xs text-slate-400">{path}</span>
        <Link
          className="inline-flex h-7 items-center gap-1.5 rounded-md bg-arch-600 px-3 text-xs font-medium text-white transition-colors hover:bg-arch-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arch-400"
          to={repoHome(wsSlug, repoSlug)}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4z" />
          </svg>
          Edit
        </Link>
      </div>
      <pre className="flex-1 overflow-auto p-4 font-mono text-[13px] leading-relaxed text-slate-300">{q.data?.content ?? ""}</pre>
    </div>
  );
}
